import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CompensationRecord,
  RecordFormPayload,
  RecordParts,
  createRecord,
  fetchRecordParts,
  formatDate,
  formatMoney,
  formatPercentFraction,
  formatSti,
  updateRecord,
} from '../../../services/adminService';
import {
  AdminFieldTw,
  AdminSectionTitle,
  ConfidenceLevel,
  ConfidenceToggle,
  btnPrimary,
  btnSecondary,
  inputClass,
  selectClass,
  textareaClass,
} from './adminUi';
import { parseNumberInput, sanitizePercentInput, todayIso } from './adminShared';

export type RecordFormMode = 'create' | 'edit';

export const RECORD_SOURCE_PRESETS = [
  { value: 'company', label: 'company' },
  { value: 'analyst', label: 'analyst' },
  { value: 'datacircle-regression', label: 'datacircle-regression' },
  { value: 'candidate', label: 'candidate' },
  { value: 'test-data', label: 'Тестовые данные' },
] as const;

/** Score 0…1 в диапазонах, согласованных с уровнями на вкладке «Рынок». */
const SOURCE_RELIABILITY_SCORE: Record<string, string> = {
  'datacircle-regression': '0.2',
  'test-data': '0.2',
  candidate: '0.4',
  analyst: '0.65',
  company: '0.9',
};

function reliabilityScoreForSource(source: string): string | null {
  return SOURCE_RELIABILITY_SCORE[source] ?? null;
}

function scoreToConfidence(score: string): ConfidenceLevel {
  const n = Number(score);
  if (n > 0.8) return 'excellent';
  if (n > 0.5) return 'high';
  if (n > 0.3) return 'moderate';
  return 'low';
}

function confidenceToScore(level: ConfidenceLevel): string {
  if (level === 'excellent') return '0.9';
  if (level === 'high') return '0.65';
  if (level === 'moderate') return '0.4';
  return '0.2';
}

export type RecordPartsFormState = {
  baseGrossSalary: string;
  salaryReviewFrequency: string;
  salaryReviewDate: string;
  salaryReviewDate2: string;
  salaryReviewCriteria: string;
  stiTargetPct: string;
  stiPayoutFrequency: string;
  stiPayoutMonth: string;
  stiPayoutMonth2: string;
  stiSizingCriteria: string;
  stiCorporateKpiExamples: string;
  stiIndividualKpiExamples: string;
  stiFormula: string;
  stiCorporateKpiWeight: string;
  stiIndividualKpiWeight: string;
  ltipForm: string;
  ltipAmount: string;
  ltipProgramDuration: string;
  ltipVestingSchedule: string;
  geography: string;
  investmentStagePriority: string;
  aumData: string;
  lifetimeCycle: string;
};

export type RecordFormState = {
  companyRoleName: string;
  source: string;
  addedDate: string;
  reliabilityScore: string;
  isApproved: boolean;
  isRoleValidated: boolean;
  firstName: string;
  lastName: string;
  middleName: string;
  phoneNumber: string;
  email: string;
  socialMedia: string;
  comment: string;
  parts: RecordPartsFormState;
};

const defaultPartsForm = (): RecordPartsFormState => ({
  baseGrossSalary: '',
  salaryReviewFrequency: '',
  salaryReviewDate: '',
  salaryReviewDate2: '',
  salaryReviewCriteria: '',
  stiTargetPct: '',
  stiPayoutFrequency: '',
  stiPayoutMonth: '',
  stiPayoutMonth2: '',
  stiSizingCriteria: '',
  stiCorporateKpiExamples: '',
  stiIndividualKpiExamples: '',
  stiFormula: '',
  stiCorporateKpiWeight: '',
  stiIndividualKpiWeight: '',
  ltipForm: '',
  ltipAmount: '',
  ltipProgramDuration: '',
  ltipVestingSchedule: '',
  geography: '',
  investmentStagePriority: '',
  aumData: '',
  lifetimeCycle: '',
});

const defaultForm = (): RecordFormState => ({
  companyRoleName: '',
  source: 'company',
  addedDate: todayIso(),
  reliabilityScore: '0.9',
  isApproved: false,
  isRoleValidated: false,
  firstName: '',
  lastName: '',
  middleName: '',
  phoneNumber: '',
  email: '',
  socialMedia: '',
  comment: '',
  parts: defaultPartsForm(),
});

function partsToForm(parts: RecordParts | null | undefined): RecordPartsFormState {
  const base = parts?.base;
  const sti = parts?.sti;
  const ltip = parts?.ltip;
  const company = parts?.companyData;
  return {
    baseGrossSalary: base?.baseGrossSalary != null ? String(base.baseGrossSalary) : '',
    salaryReviewFrequency: base?.salaryReviewFrequency || '',
    salaryReviewDate: base?.salaryReviewDate || '',
    salaryReviewDate2: base?.salaryReviewDate2 || '',
    salaryReviewCriteria: base?.salaryReviewCriteria || '',
    stiTargetPct: sti?.stiTargetPct != null ? String(sti.stiTargetPct) : '',
    stiPayoutFrequency: sti?.stiPayoutFrequency || '',
    stiPayoutMonth: sti?.stiPayoutMonth || '',
    stiPayoutMonth2: sti?.stiPayoutMonth2 || '',
    stiSizingCriteria: sti?.stiSizingCriteria || '',
    stiCorporateKpiExamples: sti?.stiCorporateKpiExamples || '',
    stiIndividualKpiExamples: sti?.stiIndividualKpiExamples || '',
    stiFormula: sti?.stiFormula || '',
    stiCorporateKpiWeight: sti?.stiCorporateKpiWeight != null ? String(sti.stiCorporateKpiWeight) : '',
    stiIndividualKpiWeight: sti?.stiIndividualKpiWeight != null ? String(sti.stiIndividualKpiWeight) : '',
    ltipForm: ltip?.ltipForm || '',
    ltipAmount: ltip?.ltipAmount != null ? String(ltip.ltipAmount) : '',
    ltipProgramDuration: ltip?.ltipProgramDuration || '',
    ltipVestingSchedule: ltip?.ltipVestingSchedule || '',
    geography: company?.geography || '',
    investmentStagePriority: company?.investmentStagePriority || '',
    aumData: company?.aumData || '',
    lifetimeCycle: company?.lifetimeCycle || '',
  };
}

function buildPartsPayload(parts: RecordPartsFormState): Pick<RecordFormPayload, 'base' | 'sti' | 'ltip' | 'companyData'> {
  const textOrNull = (v: string) => v.trim() || null;
  return {
    base: {
      baseGrossSalary: parseNumberInput(parts.baseGrossSalary),
      salaryReviewFrequency: textOrNull(parts.salaryReviewFrequency),
      salaryReviewDate: textOrNull(parts.salaryReviewDate),
      salaryReviewDate2: textOrNull(parts.salaryReviewDate2),
      salaryReviewCriteria: textOrNull(parts.salaryReviewCriteria),
    },
    sti: {
      stiTargetPct: parseNumberInput(parts.stiTargetPct),
      stiPayoutFrequency: textOrNull(parts.stiPayoutFrequency),
      stiPayoutMonth: textOrNull(parts.stiPayoutMonth),
      stiPayoutMonth2: textOrNull(parts.stiPayoutMonth2),
      stiSizingCriteria: textOrNull(parts.stiSizingCriteria),
      stiCorporateKpiExamples: textOrNull(parts.stiCorporateKpiExamples),
      stiIndividualKpiExamples: textOrNull(parts.stiIndividualKpiExamples),
      stiFormula: textOrNull(parts.stiFormula),
      stiCorporateKpiWeight: parseNumberInput(parts.stiCorporateKpiWeight),
      stiIndividualKpiWeight: parseNumberInput(parts.stiIndividualKpiWeight),
    },
    ltip: {
      ltipForm: textOrNull(parts.ltipForm),
      ltipAmount: parseNumberInput(parts.ltipAmount),
      ltipProgramDuration: textOrNull(parts.ltipProgramDuration),
      ltipVestingSchedule: textOrNull(parts.ltipVestingSchedule),
    },
    companyData: {
      geography: textOrNull(parts.geography),
      investmentStagePriority: textOrNull(parts.investmentStagePriority),
      aumData: textOrNull(parts.aumData),
      lifetimeCycle: textOrNull(parts.lifetimeCycle),
    },
  };
}

function recordToForm(record: CompensationRecord, parts?: RecordParts | null): RecordFormState {
  const source = record.source || 'company';
  return {
    companyRoleName: record.company_role_name || '',
    source,
    addedDate: record.added_date?.slice(0, 10) || todayIso(),
    reliabilityScore: reliabilityScoreForSource(source) ?? String(record.reliability_score ?? 0.4),
    isApproved: Boolean(record.is_approved),
    isRoleValidated: Boolean(record.is_role_validated),
    firstName: record.first_name || '',
    lastName: record.last_name || '',
    middleName: record.middle_name || '',
    phoneNumber: record.phone_number || '',
    email: record.email || '',
    socialMedia: record.social_media || '',
    comment: record.comment || '',
    parts: partsToForm(parts),
  };
}

function stickyAfterSave(form: RecordFormState): RecordFormState {
  const next = defaultForm();
  next.source = form.source;
  next.addedDate = form.addedDate;
  next.reliabilityScore = reliabilityScoreForSource(form.source) ?? form.reliabilityScore;
  return next;
}

function buildPayload(companyId: number, form: RecordFormState): RecordFormPayload {
  if (!form.companyRoleName.trim()) throw new Error('Укажите должность');

  const reliability = parseNumberInput(form.reliabilityScore);
  const partsPayload = buildPartsPayload(form.parts);
  return {
    companyId,
    companyRoleName: form.companyRoleName.trim(),
    source: form.source.trim() || 'company',
    addedDate: form.addedDate || todayIso(),
    reliabilityScore: reliability != null ? Math.min(1, Math.max(0, reliability)) : Number(reliabilityScoreForSource(form.source) ?? 0.4),
    isApproved: form.isApproved,
    isRoleValidated: form.isRoleValidated,
    firstName: form.firstName.trim() || null,
    lastName: form.lastName.trim() || null,
    middleName: form.middleName.trim() || null,
    phoneNumber: form.phoneNumber.trim().slice(0, 64) || null,
    email: form.email.trim().slice(0, 254) || null,
    socialMedia: form.socialMedia.trim().slice(0, 500) || null,
    comment: form.comment.trim().slice(0, 500) || null,
    ...partsPayload,
  };
}

const PANEL_WIDTH_KEY = 'dc-admin-record-panel-width';
const PANEL_MODAL_WIDTH_KEY = 'dc-admin-record-modal-width';
const PANEL_MIN_WIDTH = 320;
const PANEL_DEFAULT_WIDTH = 480;
const PANEL_MODAL_MIN_WIDTH = 560;
const PANEL_MODAL_DEFAULT_WIDTH = 880;

type PanelVariant = 'inline' | 'drawer' | 'modal';

function getPanelMinWidth(variant: PanelVariant): number {
  return variant === 'modal' ? PANEL_MODAL_MIN_WIDTH : PANEL_MIN_WIDTH;
}

function getPanelDefaultWidth(variant: PanelVariant): number {
  return variant === 'modal' ? PANEL_MODAL_DEFAULT_WIDTH : PANEL_DEFAULT_WIDTH;
}

function getPanelWidthKey(variant: PanelVariant): string {
  return variant === 'modal' ? PANEL_MODAL_WIDTH_KEY : PANEL_WIDTH_KEY;
}

function getPanelMaxWidth(variant: PanelVariant): number {
  if (typeof window === 'undefined') {
    return variant === 'modal' ? 1200 : 960;
  }
  if (variant === 'modal') {
    return Math.min(1200, Math.round(window.innerWidth * 0.92));
  }
  return Math.min(960, Math.round(window.innerWidth * 0.62));
}

function readStoredPanelWidth(variant: PanelVariant): number {
  const minW = getPanelMinWidth(variant);
  const defaultW = getPanelDefaultWidth(variant);
  try {
    const stored = localStorage.getItem(getPanelWidthKey(variant));
    if (!stored) return defaultW;
    const n = Number(stored);
    if (!Number.isFinite(n)) return defaultW;
    return Math.min(getPanelMaxWidth(variant), Math.max(minW, n));
  } catch {
    return defaultW;
  }
}

export function ResizableRecordPanelShell({
  children,
  variant = 'inline',
}: {
  children: React.ReactNode;
  variant?: 'inline' | 'drawer' | 'modal';
}) {
  const [width, setWidth] = useState(() => getPanelDefaultWidth(variant));
  const widthRef = useRef(getPanelDefaultWidth(variant));
  const [resizing, setResizing] = useState(false);
  const [canResize, setCanResize] = useState(false);

  useEffect(() => {
    const w = readStoredPanelWidth(variant);
    setWidth(w);
    widthRef.current = w;
  }, [variant]);

  useEffect(() => {
    const viewportMin = variant === 'modal' ? 640 : variant === 'drawer' ? 768 : 1024;
    const mq = window.matchMedia(`(min-width: ${viewportMin}px)`);
    const update = () => setCanResize(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [variant]);

  const startResize = (e: React.MouseEvent) => {
    if (!canResize) return;
    e.preventDefault();
    setResizing(true);
    const startX = e.clientX;
    const startW = widthRef.current;
    const maxW = getPanelMaxWidth(variant);
    const minW = getPanelMinWidth(variant);

    const onMove = (ev: MouseEvent) => {
      const next = Math.min(maxW, Math.max(minW, startW + (startX - ev.clientX)));
      widthRef.current = next;
      setWidth(next);
    };

    const onUp = () => {
      setResizing(false);
      try {
        localStorage.setItem(getPanelWidthKey(variant), String(widthRef.current));
      } catch {
        /* ignore */
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const shellClass =
    variant === 'drawer'
      ? `dc-resizable-panel dc-resizable-panel--drawer${resizing ? ' dc-resizable-panel--resizing' : ''}`
      : variant === 'modal'
        ? `dc-resizable-panel dc-resizable-panel--modal${resizing ? ' dc-resizable-panel--resizing' : ''}`
        : `dc-resizable-panel dc-resizable-panel--inline${resizing ? ' dc-resizable-panel--resizing' : ''}`;

  const isResizable = canResize;
  const shellWidthStyle =
    variant === 'modal' || isResizable
      ? { width: `${width}px`, maxWidth: 'calc(100vw - 2rem)' }
      : undefined;

  return (
    <div className={shellClass} style={shellWidthStyle}>
      {isResizable && (
        <div
          className="dc-resizable-panel-handle"
          onMouseDown={startResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Потяните, чтобы изменить ширину панели"
          title="Потяните для изменения ширины"
        />
      )}
      {children}
    </div>
  );
}

type RecordFormPanelProps = {
  mode: RecordFormMode;
  companyId: number;
  contextLabel: string;
  industryName?: string;
  sectorName?: string;
  companyName?: string;
  record?: CompensationRecord | null;
  onSaved: (saved: CompensationRecord, action: 'close' | 'another') => void;
  onCancel?: () => void;
  variant?: 'inline' | 'drawer' | 'modal';
};

export function RecordFormPanel({
  mode,
  companyId,
  contextLabel,
  industryName,
  sectorName,
  companyName,
  record,
  onSaved,
  onCancel,
  variant = 'inline',
}: RecordFormPanelProps) {
  const [form, setForm] = useState<RecordFormState>(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingParts, setLoadingParts] = useState(false);
  const roleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setErrors({});
    if (mode === 'edit' && record) {
      setLoadingParts(true);
      fetchRecordParts(record.id)
        .then((parts) => setForm(recordToForm(record, parts)))
        .catch(() => setForm(recordToForm(record, null)))
        .finally(() => setLoadingParts(false));
    } else {
      setForm(defaultForm());
      if (mode === 'create') {
        window.setTimeout(() => roleInputRef.current?.focus(), 80);
      }
    }
  }, [mode, record?.id, companyId]);

  const setField = <K extends keyof RecordFormState>(key: K, value: RecordFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  };

  const setPartField = <K extends keyof RecordPartsFormState>(key: K, value: RecordPartsFormState[K]) => {
    setForm((prev) => ({ ...prev, parts: { ...prev.parts, [key]: value } }));
  };

  const setSource = (source: string) => {
    const mappedScore = reliabilityScoreForSource(source);
    setForm((prev) => ({
      ...prev,
      source,
      ...(mappedScore != null ? { reliabilityScore: mappedScore } : {}),
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.source;
      delete next.reliabilityScore;
      return next;
    });
  };

  const sourceDrivesReliability = reliabilityScoreForSource(form.source) != null;

  const totalCompPreview = useMemo(() => {
    const base = parseNumberInput(form.parts.baseGrossSalary);
    if (base == null) return null;
    const stiPct = parseNumberInput(form.parts.stiTargetPct) ?? 0;
    const ltip = parseNumberInput(form.parts.ltipAmount) ?? 0;
    return Math.round(base + (base * stiPct) / 100 + ltip);
  }, [form.parts.baseGrossSalary, form.parts.stiTargetPct, form.parts.ltipAmount]);

  const submit = async (action: 'close' | 'another') => {
    setSubmitting(true);
    setErrors({});
    try {
      const payload = buildPayload(companyId, form);
      const saved =
        mode === 'edit' && record
          ? await updateRecord(record.id, payload)
          : await createRecord(payload);
      if (action === 'another') {
        setForm(stickyAfterSave(form));
        window.setTimeout(() => roleInputRef.current?.focus(), 50);
      }
      onSaved(saved, action);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось сохранить';
      if (message.includes('должность')) {
        setErrors({ companyRoleName: message });
      } else {
        setErrors({ _form: message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const panelClass = 'dc-record-panel';
  const sourceIsPreset = RECORD_SOURCE_PRESETS.some((p) => p.value === form.source);

  return (
    <div className={panelClass}>
      <div className="dc-record-panel-header">
        <h2 className="text-base font-semibold text-slate-900">
          {mode === 'edit' ? 'Редактирование записи' : 'Добавление записи о сотруднике'}
        </h2>
        <div className="mt-3 rounded-lg border border-dc-primary-border bg-dc-primary-soft px-3 py-2.5 text-xs text-slate-600">
          <div className="mb-1 font-medium text-dc-primary">Индустрия → Сектор → Компания</div>
          <div className="text-slate-700">
            {[industryName, sectorName, companyName].filter(Boolean).join(' → ') || contextLabel}
          </div>
        </div>
      </div>

      <div className="dc-record-panel-body">
        {loadingParts && (
          <p className="text-sm text-dc-muted">Загрузка деталей компенсации…</p>
        )}
        {errors._form && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errors._form}
          </div>
        )}

        <section>
          <AdminSectionTitle number={1} title="Сотрудник" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <AdminFieldTw label="Фамилия" dbField="compensation_records.last_name">
              <input
                className={inputClass}
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
              />
            </AdminFieldTw>
            <AdminFieldTw label="Имя" dbField="compensation_records.first_name">
              <input
                className={inputClass}
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
              />
            </AdminFieldTw>
            <AdminFieldTw label="Отчество" dbField="compensation_records.middle_name">
              <input
                className={inputClass}
                value={form.middleName}
                onChange={(e) => setField('middleName', e.target.value)}
              />
            </AdminFieldTw>
            <AdminFieldTw label="Телефон" dbField="compensation_records.phone_number">
              <input
                className={inputClass}
                type="tel"
                value={form.phoneNumber}
                onChange={(e) => setField('phoneNumber', e.target.value)}
              />
            </AdminFieldTw>
            <AdminFieldTw label="Email" dbField="compensation_records.email">
              <input
                className={inputClass}
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
              />
            </AdminFieldTw>
            <AdminFieldTw label="Соцсети" dbField="compensation_records.social_media" hint="Telegram, LinkedIn и т.п.">
              <input
                className={inputClass}
                value={form.socialMedia}
                onChange={(e) => setField('socialMedia', e.target.value)}
              />
            </AdminFieldTw>
          </div>
        </section>

        <section>
          <AdminSectionTitle number={2} title="Роль" />
          <div className="space-y-3">
            <AdminFieldTw label="Должность / роль" dbField="compensation_records.company_role_name" required error={errors.companyRoleName}>
              <input
                ref={roleInputRef}
                className={inputClass}
                value={form.companyRoleName}
                onChange={(e) => setField('companyRoleName', e.target.value)}
              />
            </AdminFieldTw>
          </div>
        </section>

        <section>
          <AdminSectionTitle number={3} title="Источник и качество данных" />
          <div className="space-y-3">
            <AdminFieldTw label="Дата получения данных" dbField="compensation_records.added_date">
              <input className={inputClass} type="date" value={form.addedDate} onChange={(e) => setField('addedDate', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Источник" dbField="compensation_records.source">
              <select className={`${selectClass} dc-select-full`} value={form.source} onChange={(e) => setSource(e.target.value)}>
                {!sourceIsPreset && form.source && (
                  <option value={form.source}>{form.source}</option>
                )}
                {RECORD_SOURCE_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </AdminFieldTw>
            <AdminFieldTw
              label="Степень уверенности"
              dbField="compensation_records.reliability_score"
              hint={sourceDrivesReliability ? 'Определяется автоматически по выбранному источнику' : undefined}
            >
              <ConfidenceToggle
                value={scoreToConfidence(form.reliabilityScore)}
                onChange={(v) => setField('reliabilityScore', confidenceToScore(v))}
                disabled={sourceDrivesReliability}
              />
            </AdminFieldTw>
          </div>
        </section>

        <section>
          <AdminSectionTitle number={4} title="Служебная информация" />
          <div className="space-y-3">
            <AdminFieldTw label="Запись валидирована" dbField="compensation_records.is_approved">
              <select
                className={`${selectClass} dc-select-full`}
                value={form.isApproved ? 'yes' : 'no'}
                onChange={(e) => setField('isApproved', e.target.value === 'yes')}
              >
                <option value="no">Нет</option>
                <option value="yes">Да</option>
              </select>
            </AdminFieldTw>
            <AdminFieldTw label="Внутренний комментарий" dbField="compensation_records.comment">
              <textarea
                className={textareaClass}
                rows={3}
                maxLength={500}
                value={form.comment}
                onChange={(e) => setField('comment', e.target.value)}
              />
              <p className="mt-1 text-right text-xs text-dc-muted">{form.comment.length}/500</p>
            </AdminFieldTw>
          </div>
        </section>

        <section>
          <AdminSectionTitle number={5} title="Base (FIX)" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <AdminFieldTw
              label="Базовый оклад (gross), ₽"
              dbField="compensation_record_base.base_gross_salary"
              hint="Годовая фиксированная часть до налогов."
            >
              <input
                className={inputClass}
                inputMode="decimal"
                placeholder="Например: 6 000 000"
                value={form.parts.baseGrossSalary}
                onChange={(e) => setPartField('baseGrossSalary', e.target.value)}
              />
            </AdminFieldTw>
            <AdminFieldTw label="Частота пересмотра оклада" dbField="compensation_record_base.salary_review_frequency">
              <input className={inputClass} value={form.parts.salaryReviewFrequency} onChange={(e) => setPartField('salaryReviewFrequency', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Месяц пересмотра" dbField="compensation_record_base.salary_review_date" hint="Текст, например: март">
              <input className={inputClass} value={form.parts.salaryReviewDate} onChange={(e) => setPartField('salaryReviewDate', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Второй месяц пересмотра" dbField="compensation_record_base.salary_review_date_2" hint="Если пересмотр 2 раза в год">
              <input className={inputClass} value={form.parts.salaryReviewDate2} onChange={(e) => setPartField('salaryReviewDate2', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Критерии пересмотра" dbField="compensation_record_base.salary_review_criteria" className="sm:col-span-2">
              <textarea className={textareaClass} rows={2} value={form.parts.salaryReviewCriteria} onChange={(e) => setPartField('salaryReviewCriteria', e.target.value)} />
            </AdminFieldTw>
          </div>
        </section>

        <section>
          <AdminSectionTitle number={6} title="STI" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <AdminFieldTw label="Целевой STI, % от base" dbField="compensation_record_sti.sti_target_pct" hint="15 = 15%. Если бонуса нет — оставьте пустым.">
              <input
                className={inputClass}
                inputMode="decimal"
                placeholder="Например: 30"
                value={form.parts.stiTargetPct}
                onChange={(e) => setPartField('stiTargetPct', sanitizePercentInput(e.target.value))}
              />
            </AdminFieldTw>
            <AdminFieldTw label="Частота выплаты" dbField="compensation_record_sti.sti_payout_frequency">
              <input className={inputClass} value={form.parts.stiPayoutFrequency} onChange={(e) => setPartField('stiPayoutFrequency', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Месяц выплаты" dbField="compensation_record_sti.sti_payout_month">
              <input className={inputClass} value={form.parts.stiPayoutMonth} onChange={(e) => setPartField('stiPayoutMonth', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Второй месяц выплаты" dbField="compensation_record_sti.sti_payout_month_2">
              <input className={inputClass} value={form.parts.stiPayoutMonth2} onChange={(e) => setPartField('stiPayoutMonth2', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Вес корп. KPI, %" dbField="compensation_record_sti.sti_corporate_kpi_weight">
              <input className={inputClass} inputMode="decimal" value={form.parts.stiCorporateKpiWeight} onChange={(e) => setPartField('stiCorporateKpiWeight', sanitizePercentInput(e.target.value))} />
            </AdminFieldTw>
            <AdminFieldTw label="Вес индив. KPI, %" dbField="compensation_record_sti.sti_individual_kpi_weight">
              <input className={inputClass} inputMode="decimal" value={form.parts.stiIndividualKpiWeight} onChange={(e) => setPartField('stiIndividualKpiWeight', sanitizePercentInput(e.target.value))} />
            </AdminFieldTw>
            <AdminFieldTw label="Критерии размера премии" dbField="compensation_record_sti.sti_sizing_criteria" className="sm:col-span-2">
              <textarea className={textareaClass} rows={2} value={form.parts.stiSizingCriteria} onChange={(e) => setPartField('stiSizingCriteria', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Корп. KPI — примеры" dbField="compensation_record_sti.sti_corporate_kpi_examples" className="sm:col-span-2">
              <textarea className={textareaClass} rows={2} value={form.parts.stiCorporateKpiExamples} onChange={(e) => setPartField('stiCorporateKpiExamples', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Индив. KPI — примеры" dbField="compensation_record_sti.sti_individual_kpi_examples" className="sm:col-span-2">
              <textarea className={textareaClass} rows={2} value={form.parts.stiIndividualKpiExamples} onChange={(e) => setPartField('stiIndividualKpiExamples', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Формула STI" dbField="compensation_record_sti.sti_formula" className="sm:col-span-2">
              <textarea className={textareaClass} rows={2} value={form.parts.stiFormula} onChange={(e) => setPartField('stiFormula', e.target.value)} />
            </AdminFieldTw>
          </div>
        </section>

        <section>
          <AdminSectionTitle number={7} title="LTIP" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <AdminFieldTw label="Форма LTIP" dbField="compensation_record_ltip.ltip_form">
              <input
                className={inputClass}
                placeholder="Carry, опционы, RSU…"
                value={form.parts.ltipForm}
                onChange={(e) => setPartField('ltipForm', e.target.value)}
              />
            </AdminFieldTw>
            <AdminFieldTw label="Размер LTIP, ₽" dbField="compensation_record_ltip.ltip_amount" hint="Годовой эквивалент, если применимо.">
              <input className={inputClass} inputMode="decimal" value={form.parts.ltipAmount} onChange={(e) => setPartField('ltipAmount', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Длительность программы" dbField="compensation_record_ltip.ltip_program_duration">
              <input className={inputClass} value={form.parts.ltipProgramDuration} onChange={(e) => setPartField('ltipProgramDuration', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Схема вестинга" dbField="compensation_record_ltip.ltip_vesting_schedule">
              <input className={inputClass} value={form.parts.ltipVestingSchedule} onChange={(e) => setPartField('ltipVestingSchedule', e.target.value)} />
            </AdminFieldTw>
          </div>
        </section>

        <section>
          <AdminSectionTitle number={8} title="Компания (данные от респондента)" />
          <p className="mb-3 text-xs text-dc-muted">
            Как респондент описывает компанию — отличается от названия компании в контексте записи.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <AdminFieldTw label="География компании" dbField="compensation_record_company_data.geography">
              <input className={inputClass} value={form.parts.geography} onChange={(e) => setPartField('geography', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Приоритетные стадии инвестирования" dbField="compensation_record_company_data.investment_stage_priority">
              <input className={inputClass} value={form.parts.investmentStagePriority} onChange={(e) => setPartField('investmentStagePriority', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="AUM компании" dbField="compensation_record_company_data.aum_data" hint="Как сообщил респондент">
              <input className={inputClass} value={form.parts.aumData} onChange={(e) => setPartField('aumData', e.target.value)} />
            </AdminFieldTw>
            <AdminFieldTw label="Жизненный цикл / стадия компании" dbField="compensation_record_company_data.lifetime_cycle">
              <input className={inputClass} value={form.parts.lifetimeCycle} onChange={(e) => setPartField('lifetimeCycle', e.target.value)} />
            </AdminFieldTw>
          </div>
        </section>

        {totalCompPreview != null && (
          <div className="rounded-lg border border-dc-primary-border bg-dc-primary-soft px-3 py-2.5 text-sm text-slate-700">
            <span className="font-medium text-dc-primary">Total Comp (расчёт):</span>{' '}
            {formatMoney(totalCompPreview)}
          </div>
        )}

        {mode === 'edit' && record && (
          <p className="text-xs text-dc-muted">
            Создано: {formatDate(record.created_at)} · Изменено: {formatDate(record.updated_at)}
            {record.grade_level != null && <> · Грейд: {record.grade_level}</>}
          </p>
        )}
      </div>

      <div className="dc-record-panel-footer">
        {mode === 'create' && (
          <button type="button" className={btnPrimary} disabled={submitting || loadingParts} onClick={() => submit('another')}>
            Сохранить и добавить ещё
          </button>
        )}
        <button type="button" className={btnSecondary} disabled={submitting || loadingParts} onClick={() => submit('close')}>
          {mode === 'edit' ? 'Сохранить' : 'Сохранить и закрыть'}
        </button>
        {onCancel && (
          <button type="button" className="dc-record-panel-cancel" onClick={onCancel} disabled={submitting}>
            Отменить
          </button>
        )}
        {mode === 'create' && (
          <p className="text-center text-xs text-dc-muted">
            После сохранения контекст компании сохраняется для быстрого ввода следующей записи.
          </p>
        )}
      </div>
    </div>
  );
}

export default function RecordDrawer(props: {
  open: boolean;
  mode: RecordFormMode;
  companyId: number;
  contextLabel: string;
  record?: CompensationRecord | null;
  onClose: () => void;
  onSaved: (saved: CompensationRecord, action: 'close' | 'another') => void;
}) {
  if (!props.open) return null;
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/30 p-4" onClick={props.onClose} role="presentation">
      <div onClick={(e) => e.stopPropagation()}>
        <ResizableRecordPanelShell variant="modal">
          <RecordFormPanel {...props} variant="modal" onCancel={props.onClose} />
        </ResizableRecordPanelShell>
      </div>
    </div>
  );
}

export function RecordPreviewModal({
  record,
  onClose,
  onEdit,
}: {
  record: CompensationRecord;
  onClose: () => void;
  onEdit: () => void;
}) {
  const [parts, setParts] = useState<RecordParts | null>(null);
  const [loadingParts, setLoadingParts] = useState(true);

  useEffect(() => {
    setLoadingParts(true);
    fetchRecordParts(record.id)
      .then(setParts)
      .catch(() => setParts(null))
      .finally(() => setLoadingParts(false));
  }, [record.id]);

  const previewRow = (label: string, value: React.ReactNode) => (
    <div key={label}>
      <span className="text-dc-muted">{label}:</span>{' '}
      <span className="text-slate-800">{value ?? '—'}</span>
    </div>
  );

  const base = parts?.base;
  const sti = parts?.sti;
  const ltip = parts?.ltip;
  const company = parts?.companyData;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} role="presentation">
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900">{record.company_role_name}</h2>
        {(record.last_name || record.first_name || record.middle_name) && (
          <p className="mt-1 text-sm text-slate-700">
            {[record.last_name, record.first_name, record.middle_name].filter(Boolean).join(' ')}
          </p>
        )}
        <p className="mt-1 text-sm text-dc-muted">
          {[record.industry_name, record.sector_name, record.company_name].filter(Boolean).join(' → ')}
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          {previewRow('Период', formatDate(record.added_date))}
          {previewRow('Источник', record.source)}
          {previewRow('FIX', formatMoney(record.fix))}
          {previewRow('STI', formatSti(record.sti))}
          {previewRow('LTIP', record.ltip ?? (record.ltip_has ? 'да' : '—'))}
          {previewRow('Надёжность', formatPercentFraction(record.reliability_score))}
          {previewRow('Грейд', record.grade_level ?? '—')}
          {previewRow('Валидирована', record.is_approved ? 'Да' : 'Нет')}
          {previewRow('Грейд подтверждён', record.is_role_validated ? 'Да' : 'Нет')}
        </div>

        {loadingParts && (
          <p className="mt-4 text-sm text-dc-muted">Загрузка деталей компенсации…</p>
        )}

        {!loadingParts && parts && (
          <div className="mt-5 space-y-4 border-t border-slate-100 pt-4 text-sm">
            <div>
              <h3 className="mb-2 font-medium text-slate-900">Base (FIX)</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {previewRow('Базовый оклад', formatMoney(base?.baseGrossSalary))}
                {previewRow('Частота пересмотра', base?.salaryReviewFrequency)}
                {previewRow('Месяц пересмотра', base?.salaryReviewDate)}
                {previewRow('Второй месяц', base?.salaryReviewDate2)}
              </div>
              {base?.salaryReviewCriteria && (
                <p className="mt-2 text-slate-700">
                  <span className="text-dc-muted">Критерии пересмотра:</span> {base.salaryReviewCriteria}
                </p>
              )}
            </div>

            <div>
              <h3 className="mb-2 font-medium text-slate-900">STI</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {previewRow('Целевой STI, %', sti?.stiTargetPct != null ? `${sti.stiTargetPct}%` : '—')}
                {previewRow('Частота выплаты', sti?.stiPayoutFrequency)}
                {previewRow('Месяц выплаты', sti?.stiPayoutMonth)}
                {previewRow('Второй месяц', sti?.stiPayoutMonth2)}
                {previewRow('Вес корп. KPI', sti?.stiCorporateKpiWeight != null ? `${sti.stiCorporateKpiWeight}%` : '—')}
                {previewRow('Вес индив. KPI', sti?.stiIndividualKpiWeight != null ? `${sti.stiIndividualKpiWeight}%` : '—')}
              </div>
              {sti?.stiSizingCriteria && previewRow('Критерии размера', sti.stiSizingCriteria)}
              {sti?.stiCorporateKpiExamples && previewRow('Корп. KPI', sti.stiCorporateKpiExamples)}
              {sti?.stiIndividualKpiExamples && previewRow('Индив. KPI', sti.stiIndividualKpiExamples)}
              {sti?.stiFormula && previewRow('Формула', sti.stiFormula)}
            </div>

            <div>
              <h3 className="mb-2 font-medium text-slate-900">LTIP</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {previewRow('Форма', ltip?.ltipForm)}
                {previewRow('Размер', formatMoney(ltip?.ltipAmount))}
                {previewRow('Длительность', ltip?.ltipProgramDuration)}
                {previewRow('Вестинг', ltip?.ltipVestingSchedule)}
              </div>
            </div>

            <div>
              <h3 className="mb-2 font-medium text-slate-900">Компания (от респондента)</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {previewRow('География', company?.geography)}
                {previewRow('Стадии инвестирования', company?.investmentStagePriority)}
                {previewRow('AUM', company?.aumData)}
                {previewRow('Жизненный цикл', company?.lifetimeCycle)}
              </div>
            </div>
          </div>
        )}

        {record.comment && (
          <p className="mt-4 text-sm text-slate-700">
            <span className="text-dc-muted">Комментарий:</span> {record.comment}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Закрыть</button>
          <button type="button" className={btnPrimary} onClick={onEdit}>Редактировать</button>
        </div>
      </div>
    </div>
  );
}
